/* eslint-disable react/prop-types, react/no-multi-comp */
import React from 'react';
import Keys, { KeyHelpers } from 'es6/core/keys';
import classnames from 'classnames';

/**
 * Click handler for the MC Choice.
 *
 * @this MCQuizChoice
 * @param {Event} e
 */
function handleClick(e) {
  e.preventDefault();

  if (this.props.disabled) {
    return;
  } else {
    this.props.onClick.call(this, e);
  }
}

/**
 * Key event handler for the MC Choice.
 *
 * @this MCQuizChoice
 * @param {Event} e
 */
function handleKeyDown(e) {
  const { keyCode } = this.props;

  if (!keyCode) {
    return;
  }

  const matchesKey = keyCode instanceof Array ?
    keyCode.indexOf(e.keyCode) !== -1 :
    keyCode === e.keyCode;

  if (matchesKey) {
    handleClick.call(this, e);
  }
}

export class MCQuizChoice extends React.Component {
  constructor(...args) {
    super(...args);

    this.state = {
      handleClick: handleClick.bind(this),
      handleKeyDown: handleKeyDown.bind(this),
    };
  }

  componentDidMount() {
    window.addEventListener('keydown', this.state.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.state.handleKeyDown);
  }

  render() {
    const className = `mc-quiz-choice ${this.props.className}`;

    return (
      <a href="#" className={className} onClick={this.state.handleClick}>{this.props.children}</a>
    );
  }
}

MCQuizChoice.propTypes = {
  className: React.PropTypes.string,
  disabled: React.PropTypes.bool,
  keyCode: React.PropTypes.oneOfType([
    React.PropTypes.number,
    React.PropTypes.array,
  ]),
  onClick: React.PropTypes.func.isRequired,
};

MCQuizChoice.defaultProps = {
  className: '',
  disabled: false,
  keyCode: [],
};

/**
 * Key press handler for the MCQuizChoices component.
 * If the user presses an arrow key, adjust focus on a child.
 * If the user presses a number, trigger the corresponding element.
 *
 * @this MCQuizChoices
 * @param {Event} e
 */
function choicesHandleKeyDown(e) {
  if (this.props.disabled) {
    return;
  }

  // If enableArrowKeys is disabled, then don't use them.
  if (!this.props.enableArrowEvents) {
    return;
  }

  const key = e.keyCode;
  const numOfChildren = this.props.children.length;
  let newIndex = this.state.focusedIndex;

  if (key === Keys.TAB) {
    // Prevent tab changing focus.
    e.preventDefault();
  }

  // User presses an arrow key or tab key.
  if (KeyHelpers.isUp(key) || (key === Keys.TAB && e.shiftKey)) {
    if (!this.state.focusedIndex) {
      newIndex = numOfChildren - 1;
    } else {
      newIndex = this.state.focusedIndex - 1;
    }
  } else if (KeyHelpers.isDown(key) || key === Keys.TAB) {
    if (this.state.focusedIndex === null || this.state.focusedIndex === numOfChildren - 1) {
      newIndex = 0;
    } else {
      newIndex = this.state.focusedIndex + 1;
    }
  }

  this.setState({ focusedIndex: newIndex });
}

export class MCQuizChoices extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      focusedIndex: props.focusedIndex,
      handleKeyDown: choicesHandleKeyDown.bind(this),
    };
  }

  componentDidMount() {
    const { focusedIndex } = this.props;

    if (focusedIndex !== null) {
      this.refs.mcQuizChoices.children[focusedIndex].focus();
    }

    window.addEventListener('keydown', this.state.handleKeyDown);
  }

  componentDidUpdate() {
    if (this.state.focusedIndex !== null) {
      this.refs.mcQuizChoices.children[this.state.focusedIndex].focus();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.state.handleKeyDown);
  }

  render() {
    const classNames = classnames('mc-quiz-choices', this.props.className, {
      'mc-quiz-choices-choosing': !this.props.disabled,
      'mc-quiz-choices-not-choosing': this.props.disabled,
    });

    return (
      <div ref="mcQuizChoices" className={classNames}>{this.props.children}</div>
    );
  }
}

MCQuizChoices.propTypes = {
  className: React.PropTypes.string,
  disabled: React.PropTypes.bool,
  enableArrowEvents: React.PropTypes.bool,
  focusedIndex: React.PropTypes.number,
};

MCQuizChoices.defaultProps = {
  className: '',
  disabled: false,
  enableArrowEvents: true,
  focusedIndex: null,
};
